import React, { useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { clearMessages } from '../../store/slices/uiSlice';
import type { RootState } from '../../store';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

const ToastContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { error, success } = useSelector((state: RootState) => state.ui);
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  useEffect(() => {
    if (success) {
      const id = `success-${Date.now()}`;
      setToasts((prev) => [...prev, { id, message: success, type: 'success', duration: 5000 }]);
      dispatch(clearMessages());
    }
    if (error) {
      const id = `error-${Date.now()}`;
      setToasts((prev) => [...prev, { id, message: error, type: 'error', duration: 5000 }]);
      dispatch(clearMessages());
    }
  }, [success, error, dispatch]);

  useEffect(() => {
    const timers: number[] = [];
    toasts.forEach((toast) => {
      if (toast.duration) {
        const timer = window.setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== toast.id));
        }, toast.duration);
        timers.push(timer);
      }
    });
    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, [toasts]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getBgColor = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getTextColor = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-gray-800';
    }
  };

  return (
    <div 
      className="fixed top-4 right-4 z-50 space-y-2"
      aria-live={toasts.some(t => t.type === 'error') ? 'assertive' : 'polite'}
      role="status"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`flex items-center gap-3 p-4 rounded-lg border shadow-lg min-w-[300px] max-w-md ${getBgColor(toast.type)} animate-in slide-in-from-right`}
        >
          {getIcon(toast.type)}
          <p className={`flex-1 text-sm font-medium ${getTextColor(toast.type)}`}>
            {toast.message}
          </p>
          <button
            onClick={() => removeToast(toast.id)}
            className={`text-gray-400 hover:text-gray-600 ${getTextColor(toast.type)}`}
            aria-label="Dismiss notification"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;


